// Turns a workout into a flat timeline of steps the session player walks through.
// Pure and deterministic: the same workout and options always give the same timeline.
//
// Each section format has one builder function in FORMAT_BUILDERS. Adding a format means adding one
// function there; a format without a builder fails with a plain UnknownFormatError instead of running wrong.
import type { Amount, Block, CardioTarget, Section, SectionFormat, SectionKind, Workout } from '../types';
import { amountSeconds, blockAmount, routineFor, withRoutines, type RoutineOptions } from '../workout';

export type StepKind = 'work' | 'rest' | 'roundRest' | 'sectionChange' | 'switchSide';

/**
 * How a step ends:
 * - timer: after the seconds (time blocks, rests)
 * - reps: when the user says all done (the count only shows how many are left)
 * - groupEnd: when the step's time box (an EMOM minute) is over; used for the rest after EMOM work
 */
export type StepEnd =
  | { kind: 'timer'; seconds: number }
  | { kind: 'reps'; reps: number; repsMax?: number }
  | { kind: 'groupEnd' };

export interface Step {
  index: number;
  kind: StepKind;
  sectionIndex: number;
  sectionId: string;
  sectionKind: SectionKind;
  blockId?: string;
  exerciseId?: string;
  note?: string;
  /** The block's amount as written (per side when perSide), for work steps. */
  amount?: Amount;
  /** 1 or 2 for blocks done per side; undefined otherwise. */
  side?: 1 | 2;
  /** Round, set or minute this step belongs to (1-based). In an AMRAP the engine tracks the live round. */
  round: number;
  /** Planned rounds, sets or minutes; null when open (AMRAP). */
  rounds: number | null;
  end: StepEnd;
  /** Planned length in seconds; reps use the planning estimate (SECONDS_PER_REP). */
  plannedSec: number;
  /** The time box this step belongs to (AMRAP, EMOM minute, non-stop section). */
  groupId?: string;
  /** A gym set given in reps: no tapping per rep, "Set done" when finished. */
  gymSet?: boolean;
  /** After this step the player asks for the reps done (and the weight): the last half of a gym set. */
  setEntry?: boolean;
  /**
   * Cardio blocks: the block's target (distance, pace or speed, zone, recovery). The step still ends on its
   * timer (the time at the target pace); a player with GPS (build step 3) ends a distance step on
   * `cardio.distanceM` instead.
   */
  cardio?: CardioTarget;
}

/** A time box: its steps run until `seconds` are over. `repeat` loops the steps until then (AMRAP). */
export interface StepGroup {
  id: string;
  sectionIndex: number;
  firstStep: number;
  lastStep: number;
  seconds: number;
  repeat: boolean;
}

export interface TimelineSection {
  id: string;
  /** Index in the sections of withRoutines(workout, options) (steps use the same index). */
  index: number;
  kind: SectionKind;
  format: SectionFormat;
  firstStep: number;
  lastStep: number;
  plannedSec: number;
}

export interface Timeline {
  workoutId: string;
  /** Changes when the workout's structure changes, so a saved session never resumes on a different timeline. */
  key: string;
  steps: Step[];
  groups: StepGroup[];
  sections: TimelineSection[];
  /** Sections left out at the start (warm-up or cool-down switched off). */
  omitted: Array<{ id: string; kind: SectionKind; index: number }>;
  /** The warm-up and cool-down routines in this plan (run or left out), by id; null: the workout's own section or none. */
  routines: { warmup: string | null; cooldown: string | null };
  plannedSec: number;
}

/**
 * The routine options (warmupRoutine, cooldownRoutine) pick another warm-up or cool-down routine than the suggested
 * one; section indices then refer to withRoutines(workout, options).sections.
 */
export interface TimelineOptions extends RoutineOptions {
  /** Pause to switch sides between the two halves of a per-side block. 0 removes the step. */
  switchSideSec?: number;
  /** Pause between sections (for example warm-up to main part). 0 removes the step. */
  sectionChangeSec?: number;
  /** Warm-up and cool-down are optional: false leaves them out. The main part always runs. */
  warmup?: boolean;
  cooldown?: boolean;
}

export const DEFAULT_TIMELINE_OPTIONS: Required<Omit<TimelineOptions, keyof RoutineOptions>> = { switchSideSec: 5, sectionChangeSec: 10, warmup: true, cooldown: true };

export class UnknownFormatError extends Error {
  constructor(readonly sectionId: string, readonly formatKind: string) {
    super(`Section "${sectionId}" uses the format "${formatKind}", which the session player cannot run yet. Add a builder for it in packages/core/src/session/timeline.ts.`);
    this.name = 'UnknownFormatError';
  }
}

type Draft = Omit<Step, 'index' | 'sectionIndex' | 'sectionId' | 'sectionKind' | 'groupId'>;

/** What a format builder uses to add steps. */
export interface BuildContext {
  /**
   * Adds the work for one block (two halves and a switch when per side).
   * `switchSides: false` goes straight to the second side (non-stop sections). `gymSet` marks a gym set:
   * one reps (and weight) entry after it, after the second side when per side.
   */
  work(block: Block, round: number, rounds: number | null, opts?: { switchSides?: boolean; gymSet?: boolean }): void;
  /** Adds a timed rest; 0 seconds adds nothing. */
  rest(seconds: number, kind?: 'rest' | 'roundRest', round?: number, rounds?: number | null): void;
  /** Adds a rest that lasts until the current time box is over. */
  restUntilGroupEnd(round: number, rounds: number | null): void;
  /** Runs `add` inside a time box of `seconds`; `repeat` loops its steps until the time is over. */
  group(seconds: number, repeat: boolean, add: () => void): void;
}

export type FormatBuilder<F extends SectionFormat = SectionFormat> = (section: Section, format: F, ctx: BuildContext) => void;

type Builders = { [K in SectionFormat['kind']]?: FormatBuilder<Extract<SectionFormat, { kind: K }>> };

function positive(n: number, what: string, sectionId: string): void {
  if (!Number.isFinite(n) || n <= 0) throw new Error(`Section "${sectionId}" needs a positive ${what}, got ${n}`);
}

/** One builder per section format. */
export const FORMAT_BUILDERS: Builders = {
  list: (s, _f, ctx) => {
    for (const b of s.blocks) ctx.work(b, 1, 1);
  },
  circuit: (s, f, ctx) => {
    positive(f.rounds, 'number of rounds', s.id);
    positive(f.workSec, 'work time', s.id);
    for (let r = 1; r <= f.rounds; r++) {
      s.blocks.forEach((b, i) => {
        ctx.work(b, r, f.rounds);
        const lastOfRound = i === s.blocks.length - 1;
        // restSec after every block, also a round's last; roundRestSec between rounds. No rest after the very
        // last block: the section break or the end of the workout follows (decision 2026-10-13).
        if (lastOfRound && r === f.rounds) return;
        if (lastOfRound && f.roundRestSec) ctx.rest(f.restSec + f.roundRestSec, 'roundRest', r, f.rounds);
        else ctx.rest(f.restSec, 'rest', r, f.rounds);
      });
    }
  },
  sets: (s, f, ctx) => {
    positive(f.sets, 'number of sets', s.id);
    for (const b of s.blocks) {
      for (let set = 1; set <= f.sets; set++) {
        // Gym sets: no tapping per rep; after the set the user confirms or corrects the reps (and the weight).
        ctx.work(b, set, f.sets, { gymSet: blockAmount(b, s).kind === 'reps' });
        if (set < f.sets) ctx.rest(f.restSec, 'rest', set, f.sets);
      }
    }
  },
  emom: (s, f, ctx) => {
    positive(f.minutes, 'number of minutes', s.id);
    for (let m = 0; m < f.minutes; m++) {
      const block = s.blocks[m % s.blocks.length]!;
      ctx.group(60, false, () => {
        ctx.work(block, m + 1, f.minutes);
        ctx.restUntilGroupEnd(m + 1, f.minutes);
      });
    }
  },
  amrap: (s, f, ctx) => {
    positive(f.minutes, 'number of minutes', s.id);
    ctx.group(f.minutes * 60, true, () => {
      for (const b of s.blocks) ctx.work(b, 1, null);
    });
  },
  continuous: (s, f, ctx) => {
    positive(f.minutes, 'number of minutes', s.id);
    // One clock for the whole section; the blocks repeat in order until it runs out. No planned rest,
    // so per-side blocks go straight to the other side. Rep blocks wait until the reps are done.
    ctx.group(f.minutes * 60, true, () => {
      for (const b of s.blocks) ctx.work(b, 1, null, { switchSides: false });
    });
  },
};

/** Every block needs a real amount: a 0 s block in a repeating time box would loop without end. */
function checkAmount(amount: Amount, blockId: string, sectionId: string): void {
  const n = amount.kind === 'time' ? amount.seconds : amount.reps;
  if (!Number.isFinite(n) || n <= 0) throw new Error(`Block "${blockId}" in section "${sectionId}" needs a positive ${amount.kind === 'time' ? 'time' : 'number of reps'}, got ${n}`);
  if (amount.kind === 'reps' && amount.repsMax !== undefined && !(amount.repsMax >= amount.reps)) throw new Error(`Block "${blockId}" in section "${sectionId}" has a rep range that ends below its start`);
}

function workEnd(amount: Amount): StepEnd {
  return amount.kind === 'time'
    ? { kind: 'timer', seconds: amount.seconds }
    : { kind: 'reps', reps: amount.reps, ...(amount.repsMax ? { repsMax: amount.repsMax } : {}) };
}

export function buildTimeline(plain: Workout, options: TimelineOptions = {}): Timeline {
  const opts = { ...DEFAULT_TIMELINE_OPTIONS, ...options };
  // The chosen (or suggested) warm-up and cool-down routines stand in for the workout's own sections of that kind.
  const workout = withRoutines(plain, options);
  const routines = {
    warmup: routineFor(plain, 'warmup', options.warmupRoutine)?.id ?? null,
    cooldown: routineFor(plain, 'cooldown', options.cooldownRoutine)?.id ?? null,
  };
  const steps: Step[] = [];
  const groups: StepGroup[] = [];
  const sections: TimelineSection[] = [];

  const omitted: Timeline['omitted'] = [];
  workout.sections.forEach((section, sectionIndex) => {
    if ((section.kind === 'warmup' && !opts.warmup) || (section.kind === 'cooldown' && !opts.cooldown)) {
      omitted.push({ id: section.id, kind: section.kind, index: sectionIndex });
      return;
    }
    if (section.blocks.length === 0) throw new Error(`Section "${section.id}" has no exercises`);
    let groupId: string | undefined;
    const push = (d: Draft) => {
      steps.push({ ...d, index: steps.length, sectionIndex, sectionId: section.id, sectionKind: section.kind, ...(groupId ? { groupId } : {}) });
    };

    if (steps.length > 0 && opts.sectionChangeSec > 0) {
      push({ kind: 'sectionChange', round: 1, rounds: 1, end: { kind: 'timer', seconds: opts.sectionChangeSec }, plannedSec: opts.sectionChangeSec });
    }
    const first = steps.length;

    const ctx: BuildContext = {
      work(block, round, rounds, wopts = {}) {
        const amount = blockAmount(block, section);
        checkAmount(amount, block.id, section.id);
        const one: Amount = { ...amount, perSide: false };
        const base = { kind: 'work' as const, blockId: block.id, exerciseId: block.exerciseId, ...(block.note ? { note: block.note } : {}), amount, round, rounds, end: workEnd(amount), plannedSec: amountSeconds(one), ...(wopts.gymSet ? { gymSet: true } : {}), ...(block.cardio ? { cardio: block.cardio } : {}) };
        const entry = wopts.gymSet ? { setEntry: true } : {};
        if (!amount.perSide) return push({ ...base, ...entry });
        push({ ...base, side: 1 });
        if (opts.switchSideSec > 0 && wopts.switchSides !== false) push({ kind: 'switchSide', blockId: block.id, exerciseId: block.exerciseId, ...(block.note ? { note: block.note } : {}), round, rounds, end: { kind: 'timer', seconds: opts.switchSideSec }, plannedSec: opts.switchSideSec });
        push({ ...base, ...entry, side: 2 });
      },
      rest(seconds, kind = 'rest', round = 1, rounds = 1) {
        if (seconds > 0) push({ kind, round, rounds, end: { kind: 'timer', seconds }, plannedSec: seconds });
      },
      restUntilGroupEnd(round, rounds) {
        push({ kind: 'rest', round, rounds, end: { kind: 'groupEnd' }, plannedSec: 0 });
      },
      group(seconds, repeat, add) {
        if (groupId) throw new Error(`Section "${section.id}": time boxes cannot be nested`);
        groupId = `${section.id}#${groups.length}`;
        const firstStep = steps.length;
        add();
        const lastStep = steps.length - 1;
        const g: StepGroup = { id: groupId, sectionIndex, firstStep, lastStep, seconds, repeat };
        groups.push(g);
        // The rest that fills an EMOM minute is planned as what the work leaves of the box.
        const inside = steps.slice(firstStep, lastStep + 1);
        const used = inside.filter((s) => s.end.kind !== 'groupEnd').reduce((sum, s) => sum + s.plannedSec, 0);
        for (const s of inside) if (s.end.kind === 'groupEnd') s.plannedSec = Math.max(0, seconds - used);
        groupId = undefined;
      },
    };

    const builder = (FORMAT_BUILDERS as Record<string, FormatBuilder | undefined>)[section.format.kind];
    if (!builder) throw new UnknownFormatError(section.id, section.format.kind);
    builder(section, section.format, ctx);

    const own = steps.slice(first);
    const boxes = groups.filter((g) => g.sectionIndex === sectionIndex);
    // A time box lasts exactly its seconds, whatever its steps would add up to (AMRAP repeats, EMOM fills).
    const plannedSec = own.filter((s) => !s.groupId).reduce((sum, s) => sum + s.plannedSec, 0) + boxes.reduce((sum, g) => sum + g.seconds, 0);
    sections.push({ id: section.id, index: sectionIndex, kind: section.kind, format: section.format, firstStep: first, lastStep: steps.length - 1, plannedSec });
  });

  const changes = steps.filter((s) => s.kind === 'sectionChange').reduce((sum, s) => sum + s.plannedSec, 0);
  const plannedSec = sections.reduce((sum, s) => sum + s.plannedSec, 0) + changes;
  // Everything that shapes the run goes into the resume key, so a saved session never resumes on an edited workout.
  const key = JSON.stringify([
    workout.id,
    steps.map((s) => [s.kind, s.sectionId, s.blockId ?? '', s.exerciseId ?? '', s.side ?? 0, s.round, s.rounds, s.end, s.groupId ?? '', s.gymSet ? 1 : 0, s.setEntry ? 1 : 0]),
    groups.map((g) => [g.id, g.firstStep, g.lastStep, g.seconds, g.repeat]),
  ]);
  if (steps.length === 0) throw new Error('This workout has nothing to run with the chosen sections');
  return { workoutId: workout.id, key: hash(key), steps, groups, sections, omitted, routines, plannedSec };
}

/** Short stable hash (FNV-1a), so the key stays small in storage. */
function hash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}
